// Custom TipTap node extensions for document block types
// that aren't covered by the StarterKit or official extensions.

import { Node, mergeAttributes } from '@tiptap/core';

// ---------------------------------------------------------------------------
// Callout — info/warning/tip block with an icon
// ---------------------------------------------------------------------------

export const CalloutNode = Node.create({
  name: 'callout',
  group: 'block',
  content: 'inline*',

  addAttributes() {
    return {
      icon: { default: '💡' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-callout]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-callout': '',
        class: 'callout flex gap-2 p-3 rounded-lg bg-muted/50 my-2',
      }),
      ['span', { class: 'callout-icon', contenteditable: 'false' }, HTMLAttributes.icon || '💡'],
      ['span', { class: 'callout-content' }, 0],
    ];
  },
});

// ---------------------------------------------------------------------------
// Toggle — collapsible/disclosure block
// ---------------------------------------------------------------------------

export const ToggleNode = Node.create({
  name: 'toggle',
  group: 'block',
  content: 'inline*',

  parseHTML() {
    return [{ tag: 'details' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'details',
      mergeAttributes(HTMLAttributes, { class: 'toggle my-2' }),
      ['summary', { class: 'cursor-pointer font-medium' }, 0],
    ];
  },
});

// ---------------------------------------------------------------------------
// Chart — atom node, rendered as a placeholder in the editor
// ---------------------------------------------------------------------------

export const ChartNode = Node.create({
  name: 'chart',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      chartType: { default: null },
      dataSource: { default: null },
      config: { default: null },
      blockId: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-chart]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-chart': '',
        class: 'chart-block flex items-center justify-center p-8 rounded-lg border border-dashed border-border my-2 text-muted-foreground',
      }),
      '📊 Chart block',
    ];
  },
});

// ---------------------------------------------------------------------------
// Diagram — atom node (mermaid / draw.io / etc.)
// ---------------------------------------------------------------------------

export const DiagramNode = Node.create({
  name: 'diagram',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      diagramType: { default: null },
      source: { default: null },
      renderedSvg: { default: null },
      blockId: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-diagram]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-diagram': '',
        class: 'diagram-block flex items-center justify-center p-8 rounded-lg border border-dashed border-border my-2 text-muted-foreground',
      }),
      '📐 Diagram block',
    ];
  },
});

// ---------------------------------------------------------------------------
// Equation — atom node for math expressions (KaTeX / MathJax)
// ---------------------------------------------------------------------------

export const EquationNode = Node.create({
  name: 'equation',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      expression: { default: '' },
      blockId: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-equation]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-equation': '',
        class: 'equation-block flex items-center justify-center p-4 rounded-lg bg-muted/30 my-2 font-mono text-sm',
      }),
      HTMLAttributes.expression || '∑ Equation',
    ];
  },
});
